import { FileButton } from '../components/FileButton';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FEATURES,
  formatBytes,
  type FeatureGrant,
  type FeatureLevel,
  type Product,
} from '@visionqc/shared';
import { api } from '../api/client';
import { useAuth } from '../api/auth';
import { CustomerPill } from '../components/CustomerPill';
import { Combobox } from '../components/Combobox';
import { COUNTRIES, CUSTOMER_HUES, countryName, customerPill } from '@visionqc/shared';
import { CURRENCIES, currencyLabel } from '../lib/currencies';
import { fmtTime } from '../lib/format';

/**
 * One customer, in full.
 *
 * Same shape as the station and user pages — identity, then what they run,
 * then what they owe. The invoices come live from the invoicing system rather
 * than a mirrored copy: a cached ledger is a ledger that will eventually
 * disagree with itself.
 */

export interface CustomerDetail {
  id: number;
  name: string;
  /** Pill tint — see packages/shared/src/customerColors.ts. */
  colorHue: number | null;
  legalName: string | null;
  vatNumber: string | null;
  registrationNumber: string | null;
  billingEmail: string | null;
  phone: string | null;
  street: string | null;
  houseNumber: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  currency: string;
  paymentTermsDays: number;
  billable: boolean;
  privacyBlur: boolean;
  invoiceCustomerId: number | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  totals: {
    inspections: number;
    bad: number;
    rejectRate: number;
    users: number;
    stations: number;
    products: number;
    datasets: number;
  };
  users: Array<Record<string, unknown>>;
  stations: Array<Record<string, unknown>>;
  products: Array<{ id: number; name: string; active: boolean; inspections: number }>;
  datasets: Array<{ id: number; name: string; images: number }>;
  periods: Array<{
    id: number;
    period: string;
    stationMonths: number;
    inspections: number;
    annotations: number;
    activeSeats: number;
    storageBytes: number;
    closedAt: string | null;
    invoiceRef: string | null;
    invoicedAt: string | null;
  }>;
}

export interface InvoiceRow {
  id: number;
  number: string;
  status: string;
  currency: string;
  totalTtc: number;
  isDraft: boolean;
  issueDate: string;
  customerName: string;
}

export interface DeviceRow {
  id: number;
  name: string;
  userId: number;
  userName: string;
  active: boolean;
  lastSeenAt: string | null;
  lastIp: string | null;
  createdAt: string;
}

function Row({ label, value, link }: { label: string; value?: string | null; link?: string | null }) {
  return (
    <tr>
      <td className="muted" style={{ width: 160, borderBottom: 'none', padding: '5px 0' }}>
        {label}
      </td>
      <td style={{ borderBottom: 'none', padding: '5px 0' }}>
        {value ? link ? <a href={link}>{value}</a> : value : <span className="muted">—</span>}
      </td>
    </tr>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint && <div className="muted" style={{ fontSize: 11 }}>{hint}</div>}
    </div>
  );
}

export function CustomerDetailView({ id, onBack }: { id: number; onBack: () => void }) {
  const [d, setD] = useState<CustomerDetail | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The invoice being read, rendered here rather than linked out to.
  const [openInvoice, setOpenInvoice] = useState<Record<string, unknown> | null>(null);
  const [showPdf, setShowPdf] = useState(false);

  const load = useCallback(async () => {
    try {
      setD(await api.customerDetail(id));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
    const inv = await api.customerInvoices(id).catch(() => null);
    setInvoices(inv?.invoices ?? []);
    setInvoiceError(inv?.error ?? (inv && !inv.configured ? 'Invoicing is not configured' : null));
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <div className="panel" style={{ color: 'var(--bad)' }}>{error}</div>;
  if (!d) return <div className="spinner">Loading customer…</div>;

  const t = d.totals;
  const thisMonth = new Date().toISOString().slice(0, 7);

  const field = (key: string, label: string, current: string | number | null, width?: number) => (
    <div style={{ flex: width ? `0 0 ${width}px` : 1, minWidth: 130 }}>
      <label>{label}</label>
      <input
        value={String(form[key] ?? current ?? '')}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%' }}
      />
    </div>
  );

  /**
   * A searchable picker for a field whose value is a code.
   *
   * Separate from field() rather than a flag on it, because what is stored is a
   * code and what is shown is a name — and the moment those two are the same
   * string you get "United States of America" in one row and "USA" in the next,
   * which is what this column already held.
   */
  const picker = (
    key: string,
    label: string,
    current: string | null,
    options: Array<{ value: string; label: string; hint?: string }>,
    width?: number,
  ) => (
    <div style={{ flex: width ? `0 0 ${width}px` : 1, minWidth: 180 }}>
      <label>{label}</label>
      <Combobox
        options={options}
        value={String(form[key] ?? current ?? '') || null}
        onChange={(v) => setForm((f) => ({ ...f, [key]: v ?? '' }))}
        placeholder={`Search ${label.toLowerCase()}…`}
        emptyLabel="— not set —"
      />
    </div>
  );

  const save = async () => {
    setBusy(true);
    try {
      await api.updateCustomer(d.id, {
        ...form,
        paymentTermsDays: form.paymentTermsDays ? Number(form.paymentTermsDays) : undefined,
      });
      setEditing(false);
      setForm({});
      await load();
      setNotice('Saved.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const closeMonth = async (period: string, close: boolean) => {
    setBusy(true);
    try {
      await api.closeUsagePeriod(d.id, period, close);
      await load();
      setNotice(close ? `${period} closed.` : `${period} recalculated.`);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sendDraft = async (periodId: number) => {
    if (!window.confirm('Send this period to invoicing as a draft?\n\nIt will not be finalised — you assign the invoice number there.')) return;
    setBusy(true);
    try {
      const r = await api.invoiceUsagePeriod(periodId);
      await load();
      setNotice(`Draft created: ${(r.invoice as { number?: string })?.number ?? 'ok'}`);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <button className="btn sm" onClick={onBack}>
            ← All customers
          </button>
          {/*
            The pill sits beside the title rather than colouring it. The title
            is text and wears text ink; the pill is the mark that carries
            identity — the same shape you saw in the Fleet list and on the
            customer list, so opening a customer confirms which one you are in
            with the tint you already learned.
          */}
          <h1 className="view-title" style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            {d.name}
            <CustomerPill name={d.name} hue={d.colorHue} />
            {!d.active && <span className="muted" style={{ fontWeight: 400 }}> · retired</span>}
          </h1>
        </div>
        <button className="btn sm" onClick={() => setEditing((v) => !v)}>
          {editing ? '✕ Cancel' : '✎ Edit'}
        </button>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div className="panel">
          <strong>Billing details</strong>
          {editing ? (
            <div className="grid" style={{ gap: 12, marginTop: 12 }}>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('name', 'Short name (dashboards)', d.name)}
                {field('legalName', 'Legal name (invoices)', d.legalName)}
              </div>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('vatNumber', 'VAT number', d.vatNumber)}
                {field('registrationNumber', 'SIRET / reg. no.', d.registrationNumber)}
              </div>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('billingEmail', 'Billing email', d.billingEmail)}
                {field('phone', 'Phone', d.phone)}
              </div>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('street', 'Street', d.street)}
                {field('houseNumber', 'No.', d.houseNumber, 90)}
                {field('postalCode', 'Postcode', d.postalCode, 120)}
              </div>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('city', 'City', d.city)}
                {picker(
                  'country',
                  'Country',
                  d.country,
                  COUNTRIES.map((c) => ({ value: c.code, label: c.name, hint: c.code })),
                )}
                {picker(
                  'currency',
                  'Currency',
                  d.currency,
                  CURRENCIES.map((c) => ({ value: c.code, label: c.name, hint: c.code })),
                  200,
                )}
                {field('paymentTermsDays', 'Terms (days)', d.paymentTermsDays, 110)}
              </div>
              <div>
                {/*
                  The six allowed tints, as the pill they will become — you pick
                  the result rather than a number. There is no free colour field
                  on purpose: green, red and amber are this product's verdict
                  language, and the database refuses them anyway (CHECK on
                  color_hue). Offering a full picker would mean offering choices
                  that come back as an error.
                */}
                <label>Pill colour</label>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  {CUSTOMER_HUES.map((h) => {
                    const chosen = Number(form.colorHue ?? d.colorHue) === h;
                    const c = customerPill(h);
                    return (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, colorHue: h }))}
                        title={`Hue ${h}`}
                        style={{
                          padding: '4px 12px',
                          borderRadius: 999,
                          background: c.background,
                          color: c.text,
                          border: `2px solid ${chosen ? c.text : c.border}`,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {d.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label>Notes</label>
                <textarea
                  rows={2}
                  value={String(form.notes ?? d.notes ?? '')}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label>Privacy</label>
                <label className="row" style={{ gap: 8, cursor: 'pointer', fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.privacyBlur ?? d.privacyBlur)}
                    onChange={(e) => setForm((f) => ({ ...f, privacyBlur: e.target.checked }))}
                  />
                  <span>Blur faces in safety footage (GDPR) — default for this customer's cameras</span>
                </label>
              </div>
              <div className="row">
                <button className="btn primary" disabled={busy} onClick={() => void save()}>
                  Save
                </button>
              </div>
            </div>
          ) : (
            <table style={{ marginTop: 8 }}>
              <tbody>
                {/* The legal name is what goes on an invoice; the short name is
                    only a dashboard label. Showing both stops them silently
                    diverging. */}
                <Row label="Legal name" value={d.legalName ?? d.name} />
                <Row label="VAT number" value={d.vatNumber} />
                <Row label="SIRET / reg. no." value={d.registrationNumber} />
                <Row
                  label="Billing email"
                  value={d.billingEmail}
                  link={d.billingEmail ? `mailto:${d.billingEmail}` : null}
                />
                <Row label="Phone" value={d.phone} link={d.phone ? `tel:${d.phone}` : null} />
                <Row
                  label="Address"
                  value={
                    [
                      [d.street, d.houseNumber].filter(Boolean).join(' '),
                      [d.postalCode, d.city].filter(Boolean).join(' '),
                      countryName(d.country),
                    ]
                      .filter(Boolean)
                      .join(', ') || null
                  }
                />
                <Row label="Currency / terms" value={`${currencyLabel(d.currency)} · ${d.paymentTermsDays} days`} />
                {d.notes && <Row label="Notes" value={d.notes} />}
              </tbody>
            </table>
          )}
        </div>

        <ProductsPanel customerId={d.id} customerName={d.name} onChanged={() => void load()} />

        <FeaturesPanel customerId={d.id} customerName={d.name} />

        <DevicesPanel customerId={d.id} users={d.users} />

        <BrandingPanel customerId={d.id} customerName={d.name} />

        <div className="panel">
          <strong>At a glance</strong>
          <div className="row" style={{ gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
            <Stat label="Inspections" value={t.inspections.toLocaleString()} />
            <Stat
              label="Rejected"
              value={t.bad.toLocaleString()}
              hint={`${(t.rejectRate * 100).toFixed(1)}%`}
            />
            <Stat label="Stations" value={String(t.stations)} />
            <Stat label="Users" value={String(t.users)} />
            <Stat label="Products" value={String(t.products)} />
            <Stat label="Datasets" value={String(t.datasets)} />
          </div>

          {d.stations.length > 0 && (
            <>
              <strong style={{ display: 'block', marginTop: 18 }}>Stations</strong>
              <table style={{ marginTop: 6 }}>
                <tbody>
                  {d.stations.map((st) => (
                    <tr key={String(st.id)}>
                      <td>
                        {String(st.name)}
                        {st.line_name ? <span className="muted"> · {String(st.line_name)}</span> : null}
                      </td>
                      <td className="muted" style={{ textAlign: 'right', fontSize: 12 }}>
                        {st.last_seen_at ? fmtTime(String(st.last_seen_at)) : 'never seen'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {d.users.length > 0 && (
            <>
              <strong style={{ display: 'block', marginTop: 18 }}>People</strong>
              <table style={{ marginTop: 6 }}>
                <tbody>
                  {d.users.map((u) => (
                    <tr key={String(u.id)}>
                      <td>
                        {[u.first_name, u.last_name].filter(Boolean).join(' ') || String(u.name)}
                        <span className="muted"> · {String(u.role)}</span>
                      </td>
                      <td className="muted" style={{ textAlign: 'right', fontSize: 12 }}>
                        {u.email ? String(u.email) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>Usage by month</strong>
          <button className="btn sm" disabled={busy} onClick={() => void closeMonth(thisMonth, false)}>
            Recalculate {thisMonth}
          </button>
        </div>
        {d.periods.length === 0 ? (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Nothing measured yet. Usage is recorded hourly; recalculate to build this month from
            what has been recorded so far.
          </p>
        ) : (
          <table style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th>Month</th>
                <th style={{ textAlign: 'right' }}>Station-months</th>
                <th style={{ textAlign: 'right' }}>Inspections</th>
                <th style={{ textAlign: 'right' }}>Seats</th>
                <th style={{ textAlign: 'right' }}>Storage</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.periods.map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.period}</strong>
                  </td>
                  <td style={{ textAlign: 'right' }}>{p.stationMonths.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{p.inspections.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{p.activeSeats}</td>
                  <td style={{ textAlign: 'right' }}>{formatBytes(p.storageBytes)}</td>
                  <td>
                    {p.invoicedAt ? (
                      <span className="badge good">invoiced</span>
                    ) : p.closedAt ? (
                      <span className="badge unsure">closed</span>
                    ) : (
                      <span className="muted">open</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!p.closedAt && (
                      <button className="btn sm" disabled={busy} onClick={() => void closeMonth(p.period, true)}>
                        Close
                      </button>
                    )}
                    {p.closedAt && !p.invoicedAt && (
                      <button className="btn sm primary" disabled={busy} onClick={() => void sendDraft(p.id)}>
                        Send draft →
                      </button>
                    )}
                    {p.invoicedAt && (
                      <span className="muted" style={{ fontSize: 12 }}>
                        {p.invoiceRef ? `#${p.invoiceRef}` : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <strong>Invoices</strong>
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Live from the invoicing system, which owns them. Drafts are finalised there — assigning a
          number is irreversible.
        </p>
        {invoiceError ? (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>⚠ {invoiceError}</p>
        ) : invoices.length === 0 ? (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>No invoices yet.</p>
        ) : (
          <table style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Number</th>
                <th>Date</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td>
                    <strong>{i.number}</strong>
                  </td>
                  <td className="muted">{i.issueDate ? fmtTime(i.issueDate) : '—'}</td>
                  <td>
                    <span className={`badge ${i.isDraft ? 'unsure' : 'good'}`}>{i.status}</span>
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {i.totalTtc.toFixed(2)} {i.currency}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn sm"
                      onClick={async () => {
                        setShowPdf(false);
                        setOpenInvoice(await api.invoiceDetail(d.id, i.id).catch(() => null));
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openInvoice && (
        <InvoicePanel
          customerId={d.id}
          invoice={openInvoice}
          showPdf={showPdf}
          onTogglePdf={() => setShowPdf((v) => !v)}
          onClose={() => setOpenInvoice(null)}
        />
      )}
    </div>
  );
}

/**
 * An invoice, shown in place.
 *
 * The lines are rendered from data so the invoice is readable immediately; the
 * PDF is fetched on demand because generating one runs a headless browser on
 * the other side and takes seconds. Both come through this app's own backend,
 * so there is no second login and no tenant to pick wrongly.
 */
function InvoicePanel({
  customerId,
  invoice,
  showPdf,
  onTogglePdf,
  onClose,
}: {
  customerId: number;
  invoice: Record<string, unknown>;
  showPdf: boolean;
  onTogglePdf: () => void;
  onClose: () => void;
}) {
  const lines = (invoice.lines ?? []) as Array<Record<string, number | string | null>>;
  const cust = invoice.customer as Record<string, string | null> | null;
  const currency = String(invoice.currency ?? 'EUR');
  const money = (v: unknown) => `${Number(v ?? 0).toFixed(2)} ${currency}`;

  return (
    <div className="panel" style={{ marginTop: 16, borderLeft: '4px solid var(--accent)' }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>
          {String(invoice.number)}{' '}
          <span className={`badge ${invoice.isDraft ? 'unsure' : 'good'}`}>
            {String(invoice.status)}
          </span>
        </strong>
        <div className="row">
          <button className="btn sm" onClick={onTogglePdf}>
            {showPdf ? 'Hide PDF' : 'Show PDF'}
          </button>
          <a
            className="btn sm"
            href={api.invoicePdfUrl(customerId, Number(invoice.id))}
            download={`${String(invoice.number)}.pdf`}
          >
            Download
          </a>
          <button className="btn sm" onClick={onClose}>
            ✕
          </button>
        </div>
      </div>

      {cust && (
        <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
          {cust.companyName}
          {cust.vatNumber ? ` · ${cust.vatNumber}` : ''}
          {cust.city ? ` · ${cust.city}` : ''}
        </p>
      )}

      <table style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th>Description</th>
            <th style={{ textAlign: 'right' }}>Qty</th>
            <th>Unit</th>
            <th style={{ textAlign: 'right' }}>Price</th>
            <th style={{ textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td>{String(l.description)}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {Number(l.quantity).toFixed(3).replace(/\.?0+$/, '')}
              </td>
              <td className="muted">{String(l.unit ?? '')}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {money(l.unitPriceHt)}
              </td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {money(l.lineHt)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right' }} className="muted">
              Subtotal
            </td>
            <td style={{ textAlign: 'right' }}>{money(invoice.subtotalHt)}</td>
          </tr>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right' }} className="muted">
              VAT
            </td>
            <td style={{ textAlign: 'right' }}>{money(invoice.totalVat)}</td>
          </tr>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right' }}>
              <strong>Total</strong>
            </td>
            <td style={{ textAlign: 'right' }}>
              <strong>{money(invoice.totalTtc)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>

      {showPdf && (
        <iframe
          title={`Invoice ${String(invoice.number)}`}
          src={api.invoicePdfUrl(customerId, Number(invoice.id))}
          style={{
            width: '100%',
            height: 700,
            marginTop: 14,
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: '#fff',
          }}
        />
      )}
    </div>
  );
}

/**
 * Which functions this tenant may use, and at what level.
 *
 * This is where a system user turns a customer into a working tenant: tick the
 * functions it bought — Etiket AI, quality inspection, workplace safety — each
 * Off, View (may see it) or Operate (may work in it). Absence is the default, so
 * a new customer starts with nothing and each function is a deliberate grant.
 *
 * The editor always shows EVERY function, because "what is this customer allowed
 * to do" is answered by the whole list, not by the rows that happen to exist.
 * Saving sends the complete set (a PUT), so an unticked function is genuinely
 * off rather than merely absent from the payload.
 *
 * Operate is offered only where it means something (FEATURES.operable) — a
 * dashboard is watched, not operated. The tenant-facing screens that read these
 * grants land in Phase 2; today this writes the entitlement the app will honour.
 */
type Level = FeatureLevel | 'off';

function FeaturesPanel({ customerId, customerName }: { customerId: number; customerName: string }) {
  const [levels, setLevels] = useState<Record<string, Level>>({});
  const [saved, setSaved] = useState<Record<string, Level>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { grants } = await api.customerFeatures(customerId);
      const map: Record<string, Level> = {};
      for (const f of FEATURES) map[f.key] = 'off';
      for (const g of grants) map[g.key] = g.level;
      setLevels(map);
      setSaved(map);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = FEATURES.some((f) => levels[f.key] !== saved[f.key]);
  const grantedCount = FEATURES.filter((f) => levels[f.key] && levels[f.key] !== 'off').length;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const grants: FeatureGrant[] = FEATURES.filter((f) => levels[f.key] !== 'off').map((f) => ({
        key: f.key,
        level: levels[f.key] as FeatureLevel,
      }));
      const res = await api.setCustomerFeatures(customerId, grants);
      const map: Record<string, Level> = {};
      for (const f of FEATURES) map[f.key] = 'off';
      for (const g of res.grants) map[g.key] = g.level;
      setLevels(map);
      setSaved(map);
      setNotice('Access saved.');
      setTimeout(() => setNotice(null), 2500);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const LEVEL_OPTS: Array<{ value: Level; label: string }> = [
    { value: 'off', label: 'Off' },
    { value: 'view', label: 'View' },
    { value: 'operate', label: 'Operate' },
  ];

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>Functions &amp; access</strong>
        <span className="muted" style={{ fontSize: 12 }}>
          {grantedCount} of {FEATURES.length} on
        </span>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        What {customerName} may use when they sign in. Off = hidden; View = may see it; Operate = may
        work in it.
      </p>
      {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 6 }}>{error}</div>}

      <div className="grid" style={{ gap: 8, marginTop: 8 }}>
        {FEATURES.map((f) => {
          const level = levels[f.key] ?? 'off';
          return (
            <div
              key={f.key}
              className="row"
              style={{ justifyContent: 'space-between', gap: 12, alignItems: 'center' }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, opacity: level === 'off' ? 0.6 : 1 }}>
                  {f.label}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {f.summary}
                </div>
              </div>
              {/* A three-way segmented control. Operate is disabled where the
                  function is view-only, so the choice offered is always one the
                  backend will accept. */}
              <div className="row" style={{ gap: 0, flexShrink: 0 }} role="group" aria-label={f.label}>
                {LEVEL_OPTS.map((opt, i) => {
                  const disabled = opt.value === 'operate' && !f.operable;
                  const active = level === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={disabled}
                      title={disabled ? 'This function is view-only' : undefined}
                      onClick={() => setLevels((m) => ({ ...m, [f.key]: opt.value }))}
                      style={{
                        padding: '4px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        borderRadius: i === 0 ? '6px 0 0 6px' : i === LEVEL_OPTS.length - 1 ? '0 6px 6px 0' : 0,
                        border: '1px solid var(--border)',
                        borderLeft: i === 0 ? '1px solid var(--border)' : 'none',
                        background: active ? 'var(--accent)' : 'transparent',
                        color: active ? '#fff' : disabled ? 'var(--muted)' : 'inherit',
                        opacity: disabled ? 0.4 : 1,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="row" style={{ marginTop: 14, gap: 10, alignItems: 'center' }}>
        <button className="btn primary sm" disabled={!dirty || busy} onClick={() => void save()}>
          Save access
        </button>
        {dirty && !busy && <span className="muted" style={{ fontSize: 12 }}>Unsaved changes</span>}
        {notice && <span style={{ color: 'var(--good)', fontSize: 12 }}>{notice}</span>}
      </div>
    </div>
  );
}

/**
 * Branding — the customer's logo for their portal.
 *
 * Their colour is the pill hue set in the edit form above; this adds the mark.
 * The portal then leads with the customer's logo and steps our wordmark back to
 * a quiet endorsement, so it reads as their product.
 */
function BrandingPanel({ customerId, customerName }: { customerId: number; customerName: string }) {
  const [bust, setBust] = useState(0);
  const [hasLogo, setHasLogo] = useState(true); // optimistic; the img's onError hides it if absent
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return setError('Choose an image first.');
    setBusy(true);
    setError(null);
    try {
      await api.uploadCustomerLogo(customerId, file);
      setHasLogo(true);
      setBust((b) => b + 1);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Remove this logo? The portal falls back to the plain wordmark.')) return;
    try {
      await api.clearCustomerLogo(customerId);
      setHasLogo(false);
      setBust((b) => b + 1);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="panel">
      <strong>Branding</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        The logo shown in {customerName}&rsquo;s portal. Their colour is the pill hue set under
        Billing details. PNG, JPG or SVG.
      </p>
      {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 6 }}>{error}</div>}
      {hasLogo && (
        <div style={{ margin: '8px 0' }}>
          <img
            src={`${api.customerLogoUrl(customerId)}?t=${bust}`}
            alt="Current logo"
            onError={() => setHasLogo(false)}
            style={{
              maxHeight: 60,
              maxWidth: 220,
              objectFit: 'contain',
              display: 'block',
              padding: 8,
              border: '1px solid var(--border)',
              borderRadius: 8,
            }}
          />
        </div>
      )}
      <div className="row" style={{ gap: 8, alignItems: 'center', marginTop: 6 }}>
        <FileButton inputRef={fileRef} accept="image/png,image/jpeg,image/svg+xml" label="Choose image" />
        <button className="btn sm primary" disabled={busy} onClick={() => void upload()}>
          {busy ? 'Uploading…' : 'Upload logo'}
        </button>
        {hasLogo && (
          <button className="btn sm" onClick={() => void clear()}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * On-site devices — how a customer's kiosk or software signs itself in.
 *
 * A device carries a key that auto-starts a portal session for one of this
 * customer's logins, so the machine at their site lands in their portal with
 * their functions, no password typed. The key is shown ONCE here, at creation,
 * with the ready-made kiosk link to paste into the on-site browser; after that
 * only its presence is known, never its value. Bound to a CUSTOMER login, so it
 * inherits exactly that account's scope.
 */
function DevicesPanel({
  customerId,
  users,
}: {
  customerId: number;
  users: Array<Record<string, unknown>>;
}) {
  const { portalUrl } = useAuth();
  const portalUsers = users.filter((u) => u.role === 'CUSTOMER' && u.active);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [userId, setUserId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [issued, setIssued] = useState<{ key: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .customerDevices(customerId)
      .then((r) => setDevices(r.devices))
      .catch(() => setDevices([]));
  }, [customerId]);
  useEffect(() => load(), [load]);

  const issue = async () => {
    if (!userId) return setError('Choose which login this device signs in as.');
    if (!name.trim()) return setError('Give the device a name.');
    setBusy(true);
    setError(null);
    try {
      const r = await api.issueDevice(customerId, userId, name.trim());
      setIssued({ key: r.key });
      setName('');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (deviceId: number) => {
    if (!window.confirm('Revoke this device? Its key stops working immediately.')) return;
    try {
      await api.revokeDevice(customerId, deviceId);
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const rotate = async (deviceId: number) => {
    if (!window.confirm("Rotate this device's key?\n\nThe old key keeps working for 7 days, so the device is not locked out before it picks up the new one.")) {
      return;
    }
    try {
      const r = await api.rotateDevice(customerId, deviceId);
      setIssued({ key: r.key });
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Point the kiosk at the tenant host when one is configured, so the customer
  // never sees "admin.…" — falls back to the current origin on a single-host setup.
  const kioskBase = portalUrl || window.location.origin;
  const kioskUrl = issued ? `${kioskBase}/?device=${issued.key}` : '';

  return (
    <div className="panel">
      <strong>Kiosk &amp; device access</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        A device signs itself in from the customer's location — their kiosk or software connects and
        is already this tenant, with their functions.
      </p>
      {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 6 }}>{error}</div>}

      {portalUsers.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
          Create a customer login first (Users) — a device signs in as one of them.
        </p>
      ) : (
        <>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
            <div style={{ minWidth: 160 }}>
              <label>Signs in as</label>
              <select
                value={userId ?? ''}
                onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— choose login —</option>
                {portalUsers.map((u) => (
                  <option key={String(u.id)} value={String(u.id)}>
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || String(u.name)}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label>Device name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Loading bay kiosk"
                style={{ width: '100%' }}
              />
            </div>
            <button className="btn primary" disabled={busy} onClick={() => void issue()}>
              Issue device
            </button>
          </div>

          {issued && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                border: '1px solid var(--accent)',
                borderRadius: 8,
                background: 'var(--panel-2, transparent)',
              }}
            >
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 13 }}>Shown once — copy it now</strong>
                <button className="btn sm" onClick={() => setIssued(null)}>
                  Done
                </button>
              </div>
              <p className="muted" style={{ fontSize: 12, margin: '6px 0' }}>
                Open this link in the browser at the customer's location. It signs the device in and
                remembers itself, so the screen stays on their portal.
              </p>
              <code
                style={{
                  display: 'block',
                  fontSize: 12,
                  wordBreak: 'break-all',
                  padding: 8,
                  borderRadius: 6,
                  background: 'rgba(0,0,0,0.25)',
                }}
              >
                {kioskUrl}
              </code>
              <button
                className="btn sm"
                style={{ marginTop: 8 }}
                onClick={() => void navigator.clipboard?.writeText(kioskUrl)}
              >
                Copy link
              </button>
            </div>
          )}
        </>
      )}

      {devices.length > 0 && (
        <table style={{ marginTop: 14 }}>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id} style={{ opacity: d.active ? 1 : 0.5 }}>
                <td>
                  {d.name}
                  <span className="muted" style={{ fontSize: 12 }}> · as {d.userName}</span>
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {d.active
                    ? d.lastSeenAt
                      ? `last seen ${fmtTime(d.lastSeenAt)}`
                      : 'never used'
                    : 'revoked'}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {d.active && (
                    <>
                      <button className="btn sm" onClick={() => void rotate(d.id)}>
                        Rotate
                      </button>{' '}
                      <button className="btn sm" onClick={() => void revoke(d.id)}>
                        Revoke
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Which products belong to this customer, and attaching the ones that belong to
 * nobody.
 *
 * The control already existed — Settings, per product, a Customer dropdown —
 * and nobody found it. Two products holding the majority of this installation's
 * inspections sat unassigned for weeks because the place you go when you think
 * "this product is theirs" is the customer, not a settings page listing every
 * product on the site.
 *
 * The same endpoint backs both. This is a second door, not a second source of
 * truth: `products.customer_id` remains the only place the fact lives.
 *
 * DATASETS ARE DELIBERATELY NOT LINKABLE HERE. A dataset belongs to a product,
 * and the product belongs to a customer. `image_sets` also carries its own
 * customer_id, and those two paths already disagree on one row — a set filed
 * under one customer whose product belongs to another. Offering a second way to
 * set it would make that worse, not better.
 */
function ProductsPanel({
  customerId,
  customerName,
  onChanged,
}: {
  customerId: number;
  customerName: string;
  onChanged: () => void;
}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.listProducts().then(setProducts).catch(() => setProducts([]));
  }, []);
  useEffect(() => load(), [load]);

  const mine = products.filter((p) => p.customerId === customerId);
  const orphans = products.filter((p) => p.customerId === null);

  const assign = async (productId: number, to: number | null) => {
    setBusy(true);
    setError(null);
    try {
      await api.setProductCustomer(productId, to);
      load();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <strong>Products</strong>
      {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginTop: 6 }}>{error}</div>}

      {mine.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
          No products yet. Until one is attached, {customerName} appears in Reporting with nothing
          under it — the inspections exist, they are just filed under no customer.
        </div>
      ) : (
        <table style={{ marginTop: 10 }}>
          <tbody>
            {mine.map((p) => (
              <tr key={p.id} style={{ cursor: 'default' }}>
                <td>{p.name}</td>
                <td className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{p.sku}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn sm" disabled={busy} onClick={() => void assign(p.id, null)}>
                    Detach
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {orphans.length > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            {orphans.length === 1 ? '1 product belongs' : `${orphans.length} products belong`} to no
            customer. Attach the ones that are {customerName}&rsquo;s.
          </div>
          <div className="grid" style={{ gap: 6 }}>
            {orphans.map((p) => (
              <div key={p.id} className="row" style={{ justifyContent: 'space-between', gap: 10 }}>
                <span style={{ fontSize: 14 }}>
                  {p.name}{' '}
                  <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                    {p.sku}
                  </span>
                </span>
                <button
                  className="btn primary sm"
                  disabled={busy}
                  onClick={() => void assign(p.id, customerId)}
                >
                  Attach →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
